import React, { useState, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Portal, Box, useDisclosure } from '@chakra-ui/react';

import { SidebarContext } from 'contexts/SidebarContext';

import Footer from 'theme-components/footer/FooterAdmin.js';
import Navbar from 'theme-components/navbar/NavbarAdmin.js';
import Sidebar from 'theme-components/sidebar/Sidebar.js';

import routes from 'routes.js';

export default function Dashboard(props) {
  const { ...rest } = props;

  const [toggleSidebar, setToggleSidebar] = useState(true);
  const { onOpen } = useDisclosure();
  const location = useLocation();

  // Filter routes for the sidebar
  const filteredRoutes = useMemo(() => {
    const filterSidebarRoutes = (routes) => {
      return routes
        .filter((route) => route.sidebar !== false)
        .map((route) => {
          if (route.children) {
            return { ...route, children: filterSidebarRoutes(route.children) };
          }
          return route;
        });
    };

    return filterSidebarRoutes(routes);
  }, []);

  const isFullScreenRoute = useMemo(
    () => location.pathname === '/admin/full-screen-maps',
    [location.pathname],
  );

  const activeRoute = useMemo(() => {
    const findActiveRoute = (routes, nested = false) => {
      for (const route of routes) {
        if (
          location.pathname.startsWith(
            `${route.layout}${route.path.replace(/:\w+/g, '')}`,
          )
        ) {
          return route.name;
        }
        if (route.children) {
          const activeChild = findActiveRoute(route.children, true);
          if (activeChild) {
            return activeChild;
          }
        }
      }
      return !nested ? 'Default Text' : null;
    };
    return findActiveRoute(routes);
  }, [location.pathname]);

  const activeNavbarProps = useMemo(() => {
    const findNavbarProps = (routes) => {
      for (const route of routes) {
        if (
          location.pathname.startsWith(
            `${route.layout}${route.path.replace(/:\w+/g, '')}`,
          )
        ) {
          return {
            secondary: route.secondary || false,
            message: route.messageNavbar || '',
          };
        }
        if (route.children) {
          const activeChild = findNavbarProps(route.children);
          if (activeChild) {
            return activeChild;
          }
        }
      }
      return { secondary: false, message: '' };
    };
    return findNavbarProps(routes);
  }, [location.pathname]);

  const routeComponents = useMemo(() => {
    const generateRoutes = (routes) => {
      return routes.flatMap((route, key) => {
        const currentRoute =
          route.layout === '/admin' ? (
            <Route
              path={`${route.layout}${route.path}`}
              element={route.component}
              key={key}
            />
          ) : null;

        const childRoutes = route.children ? generateRoutes(route.children) : [];
        return [currentRoute, ...childRoutes];
      });
    };

    return generateRoutes(routes);
  }, []);

  return (
    <Box>
      <SidebarContext.Provider value={{ toggleSidebar, setToggleSidebar }}>
        <Sidebar routes={filteredRoutes} {...rest} />
        <Box
          float="right"
          minHeight="100vh"
          height="100vh"
          overflow="auto"
          position="relative"
          maxHeight="100%"
          w={{
            base: '100%',
            xl: toggleSidebar ? 'calc(100% - 290px)' : '100%',
          }}
          transition="all 0.33s ease"
        >
          <Portal>
            <Navbar
              onOpen={onOpen}
              logoText="Horizon UI Dashboard PRO"
              brandText={activeRoute}
              {...activeNavbarProps}
              {...rest}
            />
          </Portal>
          {!isFullScreenRoute && (
            <Box 
              mx="auto" 
              p={{ base: '20px', md: '30px' }} 
              pt="50px"
              minH="calc(100vh - 50px)"
              display="flex"
              flexDirection="column"
            >
              <Routes>
                {routeComponents}
                <Route path="/" element={<Navigate to="/admin/dashboard" replace />} />
              </Routes>
            </Box>
          )}
          <Footer />
        </Box>
      </SidebarContext.Provider>
    </Box>
  );
}
